import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Plus, Mail, Edit2, Trash2, RefreshCw, Clock, CheckCircle, XCircle, ClipboardList } from 'lucide-react';
import './StaffManagementTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const StaffManagementTab = () => {
  const { token } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'receptionist',
    salary: ''
  });
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedStaffForTask, setSelectedStaffForTask] = useState(null);
  const [showTaskHistoryModal, setShowTaskHistoryModal] = useState(false);
  const [selectedStaffForHistory, setSelectedStaffForHistory] = useState(null);
  const [taskHistory, setTaskHistory] = useState([]);
  const [taskHistoryLoading, setTaskHistoryLoading] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    priority: 3,
    estimatedDuration: '',
    dueDate: '',
    roomId: '',
    notes: ''
  });
  const [staffTaskCounts, setStaffTaskCounts] = useState({});
  const [availableRooms, setAvailableRooms] = useState([]);

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : null;

  const roles = [
    { value: 'receptionist', label: 'Receptionist', icon: '🏨' },
    { value: 'housekeeping', label: 'Housekeeping', icon: '🧹' },
    { value: 'maintenance', label: 'Maintenance', icon: '🔧' },
    { value: 'security', label: 'Security', icon: '🔒' },
    { value: 'chef', label: 'Chef', icon: '👨‍🍳' },
    { value: 'waiter', label: 'Waiter', icon: '🍽️' }
  ];

  useEffect(() => {
    if (token) {
      fetchStaff();
      fetchTaskCounts();
      fetchAvailableRooms();
    }
  }, [token]);

  const fetchTaskCounts = async () => {
    if (!auth) {
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/manager/tasks`, auth);
      if (res.data.success) {
        const tasks = Array.isArray(res.data.data)
          ? res.data.data
          : (res.data.data?.tasks || []);
        // Count pending/assigned tasks per employee
        const counts = {};
        tasks.forEach(task => {
          if (task.status === 'assigned' || task.status === 'in_progress') {
            counts[task.employee_id] = (counts[task.employee_id] || 0) + 1;
          }
        });
        setStaffTaskCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching task counts:', error);
    }
  };
  const fetchAvailableRooms = async () => {
    if (!auth) {
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/manager/rooms`, auth);
      if (res.data.success) {
        const rooms = Array.isArray(res.data.data)
          ? res.data.data
          : (res.data.data?.rooms || []);
        const normalizedRooms = rooms
          .map(room => ({
            ...room,
            room_id: room.room_id ?? room.id,
            floor: room.floor ?? room.floor_name ?? null,
            room_type: room.room_type ?? room.type ?? 'Room'
          }))
          .filter(room => room.room_id !== undefined && room.room_id !== null);

        setAvailableRooms(normalizedRooms);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };
  const fetchStaff = async () => {
    if (!auth) {
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/manager/staff`, auth);
      if (res.data.success) {
        // Handle both response formats: direct array or nested object
        const staffData = Array.isArray(res.data.data) 
          ? res.data.data 
          : (res.data.data?.staff || []);
        console.log('Fetched staff data:', staffData);
        console.log('First staff member:', staffData[0]);
        setStaff(staffData);
      } else {
        console.error('Invalid response format:', res.data);
        setStaff([]);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaff([]);
      alert('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (!auth) {
        return;
      }

      if (editingStaff) {
        // Update existing staff
        const res = await axios.put(
          `${API_URL}/manager/staff/${editingStaff.employee_id}`,
          formData,
          auth
        );
        
        if (res.data.success) {
          alert('Staff member updated successfully!');
          closeModal();
          fetchStaff();
        }
      } else {
        // Add new staff
        const res = await axios.post(
          `${API_URL}/manager/staff`,
          formData,
          auth
        );
        
        if (res.data.success) {
          setGeneratedPassword(res.data.data.password);
          // Don't close modal yet - show password first
          fetchStaff();
        }
      }
    } catch (error) {
      console.error('Error saving staff:', error);
      alert(error.response?.data?.message || 'Failed to save staff member');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (employeeId, name) => {    if (!employeeId) {
      console.error('Cannot delete: employee ID is undefined', { employeeId, name });
      alert('Error: Invalid employee ID. Please refresh the page and try again.');
      return;
    }
    if (!window.confirm(`Are you sure you want to remove ${name} from the staff?`)) {
      return;
    }

    if (!auth) {
      return;
    }

    try {
      const res = await axios.delete(
        `${API_URL}/manager/staff/${employeeId}`,
        auth
      );
      
      if (res.data.success) {
        alert('Staff member removed successfully');
        fetchStaff();
      }
    } catch (error) {
      console.error('Error deleting staff:', error);
      alert('Failed to remove staff member');
    }
  };

  const openTaskModal = (staffMember) => {
    setSelectedStaffForTask(staffMember);
    setTaskFormData({
      title: '',
      description: '',
      priority: 3,
      estimatedDuration: '',
      dueDate: '',
      roomId: '',
      notes: ''
    });
    setShowTaskModal(true);
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setSelectedStaffForTask(null);
    setTaskFormData({
      title: '',
      description: '',
      priority: 3,
      estimatedDuration: '',
      dueDate: '',
      roomId: '',
      notes: ''
    });
  };

  const openTaskHistoryModal = async (staffMember) => {
    if (!auth || !staffMember?.employee_id) {
      return;
    }

    setSelectedStaffForHistory(staffMember);
    setShowTaskHistoryModal(true);
    setTaskHistoryLoading(true);

    try {
      const res = await axios.get(
        `${API_URL}/manager/tasks`,
        {
          ...auth,
          params: { employeeId: staffMember.employee_id }
        }
      );

      if (res.data.success) {
        const tasks = Array.isArray(res.data.data)
          ? res.data.data
          : (res.data.data?.tasks || []);
        setTaskHistory(tasks);
      } else {
        setTaskHistory([]);
      }
    } catch (error) {
      console.error('Error fetching task history:', error);
      setTaskHistory([]);
      alert(error.response?.data?.message || 'Failed to load task history');
    } finally {
      setTaskHistoryLoading(false);
    }
  };

  const closeTaskHistoryModal = () => {
    setShowTaskHistoryModal(false);
    setSelectedStaffForHistory(null);
    setTaskHistory([]);
  };

  const getTaskStatusBadge = (status) => {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'completed') return <span className="status-badge active">Completed</span>;
    if (normalized === 'in_progress') return <span className="status-badge pending">In Progress</span>;
    if (normalized === 'cancelled') return <span className="status-badge terminated">Cancelled</span>;
    return <span className="status-badge pending">Assigned</span>;
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    
    if (!auth || !selectedStaffForTask) {
      return;
    }

    try {
      setLoading(true);
      
      const taskData = {
        employeeId: selectedStaffForTask.employee_id,
        title: taskFormData.title,
        description: taskFormData.description,
        priority: parseInt(taskFormData.priority),
        estimatedDuration: taskFormData.estimatedDuration ? parseInt(taskFormData.estimatedDuration) : null,
        dueDate: taskFormData.dueDate || null,
        roomId: taskFormData.roomId ? parseInt(taskFormData.roomId) : null,
        notes: taskFormData.notes
      };

      const res = await axios.post(
        `${API_URL}/manager/tasks`,
        taskData,
        auth
      );
      
      if (res.data.success) {
        alert(`Task "${taskFormData.title}" assigned successfully to ${selectedStaffForTask.name}`);
        closeTaskModal();
        fetchTaskCounts(); // Refresh task counts
      }
    } catch (error) {
      console.error('Error assigning task:', error);
      alert(error.response?.data?.message || 'Failed to assign task');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (employeeId, name) => {
    if (!window.confirm(`Resend invitation email to ${name}?`)) {
      return;
    }

    if (!auth) {
      return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/manager/staff/${employeeId}/resend-invitation`,
        {},
        auth
      );
      
      if (res.data.success) {
        alert(`Invitation resent successfully!\nNew Password: ${res.data.data.password}\n\nPlease share this password with the staff member.`);
        fetchStaff();
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Failed to resend invitation');
    }
  };

  const openAddModal = () => {
    setEditingStaff(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'receptionist',
      salary: ''
    });
    setGeneratedPassword(null);
    setShowAddModal(true);
  };

  const openEditModal = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      phone: staffMember.phone || '',
      role: staffMember.role,
      salary: staffMember.salary || ''
    });
    setGeneratedPassword(null);
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingStaff(null);
    setGeneratedPassword(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'receptionist',
      salary: ''
    });
  };

  const getRoleInfo = (role) => {
    return roles.find(r => r.value === role) || { label: role, icon: '👤' };
  };

  const getStatusBadge = (status, invitationStatus) => {
    if (status === 'terminated') {
      return <span className="status-badge terminated">Terminated</span>;
    }
    
    if (invitationStatus === 'pending') {
      return <span className="status-badge pending">Pending Invitation</span>;
    }
    
    if (invitationStatus === 'accepted') {
      return <span className="status-badge active">Active</span>;
    }
    
    return <span className="status-badge active">Active</span>;
  };

  return (
    <div className="staff-management-tab">
      <div className="tab-header">
        <div className="header-left">
          <Users size={28} />
          <div>
            <h2>Staff Management</h2>
            <p>Manage your hotel staff members</p>
          </div>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={18} />
          Add Staff Member
        </button>
      </div>

      {loading && !showAddModal ? (
        <div className="loading-state">
          <RefreshCw className="spinning" size={32} />
          <p>Loading staff...</p>
        </div>
      ) : (
        <div className="staff-grid">
          {!Array.isArray(staff) || staff.length === 0 ? (
            <div className="empty-state">
              <Users size={64} />
              <h3>No Staff Members</h3>
              <p>Add your first staff member to get started</p>
              <button className="btn-secondary" onClick={openAddModal}>
                <Plus size={18} />
                Add Staff Member
              </button>
            </div>
          ) : (
            staff.map(member => {
              const roleInfo = getRoleInfo(member.role);
              // Debug: Check if employee_id exists
              if (!member.employee_id) {
                console.warn('Staff member missing employee_id:', member);
              }
              return (
                <div key={member.employee_id || member.user_id} className="staff-card">
                  <div className="staff-header">
                    <div className="staff-avatar">
                      <span className="role-icon">{roleInfo.icon}</span>
                      {staffTaskCounts[member.employee_id] > 0 && (
                        <span className="task-badge" title={`${staffTaskCounts[member.employee_id]} active task(s)`}>
                          {staffTaskCounts[member.employee_id]}
                        </span>
                      )}
                    </div>
                    <div className="staff-info">
                      <h3>{member.name}</h3>
                      <p className="staff-role">{roleInfo.label}</p>
                    </div>
                  </div>

                  <div className="staff-details">
                    <div className="detail-item">
                      <Mail size={16} />
                      <span>{member.email}</span>
                    </div>
                    {member.phone && (
                      <div className="detail-item">
                        <span>📞</span>
                        <span>{member.phone}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <Clock size={16} />
                      <span>Joined: {new Date(member.hire_date).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div className="staff-status">
                    {getStatusBadge(member.status, member.invitation_status)}
                    {member.last_login && (
                      <span className="last-login">
                        Last login: {new Date(member.last_login).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  <div className="staff-actions">
                    <button
                      className="btn-icon btn-history"
                      onClick={() => openTaskHistoryModal(member)}
                      title="View Task History"
                    >
                      <ClipboardList size={16} />
                    </button>
                    <button
                      className="btn-icon btn-task"
                      onClick={() => openTaskModal(member)}
                      title="Assign Task"
                    >
                      📋
                    </button>
                    {member.invitation_status === 'pending' && (
                      <button
                        className="btn-icon btn-resend"
                        onClick={() => handleResendInvitation(member.employee_id, member.name)}
                        title="Resend Invitation"
                      >
                        <Mail size={16} />
                      </button>
                    )}
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => openEditModal(member)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(member.employee_id, member.name)}
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            {generatedPassword ? (
              <div className="password-display">
                <CheckCircle size={48} className="success-icon" />
                <h3>Staff Member Added Successfully!</h3>
                <p>An invitation email has been sent to {formData.email}</p>
                
                <div className="password-box">
                  <h4>Temporary Password:</h4>
                  <div className="password-value">{generatedPassword}</div>
                  <p className="password-note">
                    ⚠️ Please share this password with the staff member.
                    They can use it to log in to the Staff App.
                  </p>
                </div>

                <button className="btn-primary" onClick={closeModal}>
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Enter staff member's full name"
                  />
                </div>

                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1234567890"
                  />
                </div>

                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role}
                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.icon} {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Salary (Optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.salary}
                    onChange={e => setFormData({ ...formData, salary: e.target.value })}
                    placeholder="Enter monthly salary"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : (editingStaff ? 'Update' : 'Add Staff Member')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Task Assignment Modal */}
      {showTaskModal && selectedStaffForTask && (
        <div className="modal-overlay" onClick={closeTaskModal}>
          <div className="modal-content task-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Assign Task to {selectedStaffForTask.name}</h2>
              <button className="close-btn" onClick={closeTaskModal}>×</button>
            </div>

            <form onSubmit={handleTaskSubmit}>
              <div className="form-group">
                <label>Task Title *</label>
                <input
                  type="text"
                  value={taskFormData.title}
                  onChange={e => setTaskFormData({ ...taskFormData, title: e.target.value })}
                  required
                  placeholder="e.g., Clean Room 305, Fix AC in Lobby"
                  maxLength="200"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={taskFormData.description}
                  onChange={e => setTaskFormData({ ...taskFormData, description: e.target.value })}
                  placeholder="Provide detailed instructions for this task..."
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    value={taskFormData.priority}
                    onChange={e => setTaskFormData({ ...taskFormData, priority: e.target.value })}
                    required
                  >
                    <option value="1">🔴 Urgent (1)</option>
                    <option value="2">🟠 High (2)</option>
                    <option value="3">🟡 Medium (3)</option>
                    <option value="4">🟢 Low (4)</option>
                    <option value="5">⚪ Very Low (5)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Estimated Duration (minutes)</label>
                  <input
                    type="number"
                    value={taskFormData.estimatedDuration}
                    onChange={e => setTaskFormData({ ...taskFormData, estimatedDuration: e.target.value })}
                    placeholder="30"
                    min="1"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Due Date/Time</label>
                  <input
                    type="datetime-local"
                    value={taskFormData.dueDate}
                    onChange={e => setTaskFormData({ ...taskFormData, dueDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Room (Optional)</label>
                  <select
                    value={taskFormData.roomId}
                    onChange={e => setTaskFormData({ ...taskFormData, roomId: e.target.value })}
                  >
                    <option value="">-- No specific room --</option>
                    {availableRooms.map(room => (
                      <option key={room.room_id} value={room.room_id}>
                        Room {room.room_number} - Floor {room.floor ?? '-'} ({room.room_type})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  value={taskFormData.notes}
                  onChange={e => setTaskFormData({ ...taskFormData, notes: e.target.value })}
                  placeholder="Any special instructions or notes..."
                  rows="3"
                />
              </div>

              <div className="task-summary">
                <h4>Task Summary</h4>
                <div className="summary-item">
                  <span className="summary-label">Assigned to:</span>
                  <span className="summary-value">{selectedStaffForTask.name} ({getRoleInfo(selectedStaffForTask.role).label})</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Staff Email:</span>
                  <span className="summary-value">{selectedStaffForTask.email}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Notification:</span>
                  <span className="summary-value">✅ Staff will receive push notification</span>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeTaskModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Assigning...' : 'Assign Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task History Modal */}
      {showTaskHistoryModal && selectedStaffForHistory && (
        <div className="modal-overlay" onClick={closeTaskHistoryModal}>
          <div className="modal-content task-history-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🕘 Task History - {selectedStaffForHistory.name}</h2>
              <button className="close-btn" onClick={closeTaskHistoryModal}>×</button>
            </div>

            <div className="task-history-body">
              {taskHistoryLoading ? (
                <div className="loading-state">
                  <RefreshCw className="spinning" size={28} />
                  <p>Loading task history...</p>
                </div>
              ) : taskHistory.length === 0 ? (
                <div className="empty-state compact">
                  <ClipboardList size={48} />
                  <h3>No tasks found</h3>
                  <p>No tasks have been assigned to this staff member yet.</p>
                </div>
              ) : (
                <div className="task-history-list">
                  {taskHistory.map(task => (
                    <div key={task.task_id} className="task-history-item">
                      <div className="task-history-top">
                        <h4>{task.title}</h4>
                        {getTaskStatusBadge(task.status)}
                      </div>
                      {task.description && <p className="task-desc">{task.description}</p>}
                      <div className="task-meta">
                        <span><strong>Priority:</strong> {task.priority}</span>
                        <span><strong>Assigned:</strong> {task.assigned_at ? new Date(task.assigned_at).toLocaleString() : 'N/A'}</span>
                        <span><strong>Due:</strong> {task.due_date ? new Date(task.due_date).toLocaleString() : 'N/A'}</span>
                        <span><strong>Room:</strong> {task.room_number ? `Room ${task.room_number}` : 'N/A'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeTaskHistoryModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffManagementTab;
